import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError, switchMap, tap, map, catchError } from 'rxjs';
import { RagApiService, ChatHistory, PollResponse, IssueDetailPayload, ApiError, ApiMode } from './rag-api.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private chatHistory = new BehaviorSubject<ChatHistory[]>([]);
  chatHistory$ = this.chatHistory.asObservable();

  constructor(private ragApiService: RagApiService) {}

  preloadChat(question: string, answer: string | null, conversationId: string): Observable<boolean> {
    this.chatHistory.next([]);
    
    this.ragApiService.storeConversation(conversationId);
    
    if (question && answer) {
      const history = this.chatHistory.value;
      history.push({ 
        question: question, 
        answer: answer 
      });
      this.chatHistory.next(history);
    }
    
    return of(true);
  }

  loadIssueDetails(issueKey: string): Observable<IssueDetailPayload> {
    return this.ragApiService.getIssueDetails(issueKey).pipe(
      tap(response => {
        console.log('Issue details loaded:', response);
        this.chatHistory.next([]);
        if (response.question && response.answer) {
          const history = this.chatHistory.value;
          history.push({ 
            question: response.question, 
            answer: response.answer 
          });
          this.chatHistory.next(history);
        }
      }),
      catchError((error: ApiError) => {
        console.error('Error in loadIssueDetails:', error);
        return throwError(() => error);
      })
    );
  }

  sendInitialQuestion(question: string): Observable<string> {
    const request = {
      actor: 'user',
      question,
      history: [],
    };

    return this.ragApiService.predict(request, ApiMode.OPERATOR).pipe(
      tap(response => {
        console.log('Predict response in chat service:', response);
      }),
      switchMap(response => {
        if (!response.promptID) {
          throw new Error('No promptID received from predict');
        }
        console.log('Using promptID for polling:', response.promptID);
        return this.ragApiService.pollResponse(response.promptID, ApiMode.OPERATOR);
      }),
      map((response: PollResponse) => response.output),
      tap((answer) => {
        console.log('Got answer:', answer);
        const history = this.chatHistory.value;
        history.push({ question, answer });
        this.chatHistory.next(history);
      }),
      catchError((error: ApiError) => {
        console.error('Error in sendInitialQuestion:', error);
        return throwError(() => error);
      })
    );
  }

  continueConversation(question: string): Observable<string> {
    const conversationId = this.ragApiService.getStoredConversationId();
    if (!conversationId) {
      return throwError(() => ({ 
        status: 400, 
        message: 'No conversation ID found',
        detail: 'You need to start a new conversation' 
      } as ApiError));
    }

    const request = {
      actor: 'user',
      question,
      history: this.chatHistory.value,
      load_history: true,
    };

    return this.ragApiService.continue(conversationId, request, ApiMode.OPERATOR).pipe(
      switchMap(response => {
        if (!response.promptID) {
          throw new Error('No promptID received from continue');
        }
        console.log('Using promptID for polling:', response.promptID);
        return this.ragApiService.pollResponse(response.promptID, ApiMode.OPERATOR);
      }),
      map((response: PollResponse) => response.output),
      tap((answer) => {
        console.log('Got answer:', answer);
        const history = this.chatHistory.value;
        history.push({ question, answer });
        this.chatHistory.next(history);
      }),
      catchError((error: ApiError) => {
        console.error('Error in continueConversation:', error);
        return throwError(() => error);
      })
    );
  }

  clearChat(): void {
    this.chatHistory.next([]);
    this.ragApiService.clearStoredIds();
  }
}